// crewbit-admin/src/components/money/settings/NotificationsPanel.tsx
//
// Notifications group sub-screen body. 4 NotificationToggleRow + quiet hours
// LEARN row per §3i.
//
// Inline-save per Decision §Settings.D3: each toggle owns its own debounce
// + SaveConfirmationPill via NotificationToggleRow primitive. This panel
// just wires hooks and copy.
//
// Mirrors crewbit-mobile/components/money/settings/NotificationsPanel.tsx.

import { useCallback } from 'react';
import { Bell, Clock } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import { Skeleton } from '../../common/Skeleton';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import { useUpdateUserMoneyPreferences } from '../../../hooks/useUpdateUserMoneyPreferences';
import { lookupErrorCopy } from '../../../lib/errorMap';
import NotificationToggleRow, { type NotificationToggleSaveResult } from './NotificationToggleRow';
import SettingsStatusPill from './SettingsStatusPill';

type ToggleField =
  | 'notificationPaymentsReceived'
  | 'notificationDisputesOpened'
  | 'notificationTaxDue'
  | 'notificationPayoutsArrived'
  | 'notificationAccountantInvited'
  | 'notificationAccountantAvailable';

export default function NotificationsPanel() {
  const prefs = useUserPreferences();
  const updatePrefs = useUpdateUserMoneyPreferences();

  const wrapSave = useCallback(
    (field: ToggleField) =>
      async (next: boolean): Promise<NotificationToggleSaveResult> => {
        const result = await updatePrefs({ [field]: next });
        if (result.success) return { success: true };
        return {
          success: false,
          message: lookupErrorCopy('update_user_money_preferences', result.error.code),
        };
      },
    [updatePrefs],
  );

  if (prefs.isLoading && !prefs.data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <Skeleton height={320} />
        <Skeleton height={100} />
      </div>
    );
  }

  const data = prefs.data;
  if (!data) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      <div style={{
        background: palette.white,
        borderRadius: radii.lg,
        border: `1px solid ${palette.g150}`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: spacing.xs,
          paddingInline: spacing.md, paddingTop: spacing.md, paddingBottom: spacing.sm,
        }}>
          <Bell size={16} color={palette.g600} strokeWidth={2} />
          <span style={{ fontSize: typography.size.heading, fontWeight: 700, color: palette.g800 }}>
            Money notifications
          </span>
        </div>

        <NotificationToggleRow
          label="Payments received"
          description="Email when a customer pays an invoice (online or marked paid manually)."
          value={data.notificationPaymentsReceived}
          onSave={wrapSave('notificationPaymentsReceived')}
        />
        <Divider />
        <NotificationToggleRow
          label="Disputes opened"
          description="Email when a customer disputes a charge through their bank or card issuer."
          value={data.notificationDisputesOpened}
          onSave={wrapSave('notificationDisputesOpened')}
        />
        <Divider />
        <NotificationToggleRow
          label="Tax due reminders"
          description="Email 30, 14, and 3 days before your HST/GST remittance is due."
          value={data.notificationTaxDue}
          onSave={wrapSave('notificationTaxDue')}
        />
        <Divider />
        <NotificationToggleRow
          label="Payouts arrived"
          description="Email when a Stripe payout lands in your bank account."
          value={data.notificationPayoutsArrived}
          onSave={wrapSave('notificationPayoutsArrived')}
        />
        <Divider />
        <NotificationToggleRow
          label="Accountant invited"
          description="Email when you create an accountant invite (so you have a record of the share link)."
          value={data.notificationAccountantInvited}
          onSave={wrapSave('notificationAccountantInvited')}
        />
        <Divider />
        <NotificationToggleRow
          label="Accountant available"
          description="Email when an accountant claims your invite and is ready to access your books."
          value={data.notificationAccountantAvailable}
          onSave={wrapSave('notificationAccountantAvailable')}
        />
      </div>

      <div style={{
        background: palette.white,
        borderRadius: radii.lg,
        border: `1px solid ${palette.g150}`,
        padding: spacing.md,
      }}>
        <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 18,
            background: palette.accentWash,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Clock size={18} color={palette.accent} strokeWidth={2} />
          </div>
          <div style={{
            flex: 1, minWidth: 200,
            display: 'flex', flexDirection: 'column', gap: spacing.xxs,
          }}>
            <span style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
              Quiet hours
            </span>
            <span style={{ fontSize: typography.size.caption, color: palette.g500 }}>
              Pause non-urgent emails (payouts, reminders) outside working hours.
              Disputes still page immediately. Coming with the notification preferences refresh.
            </span>
          </div>
          <SettingsStatusPill kind="learn" label="Coming soon" />
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div style={{
      height: 1,
      background: palette.g150,
      marginInlineStart: spacing.md,
    }} />
  );
}
